import { Button } from '@intigral/shared/components/Button';
import { FontIcon } from '@intigral/shared/components/FontIcon';
import { ButtonTypes } from '../../config/constants';
import { checkIfReminderExists, reminderItemMixpanelProperties } from '../../helpers/reminder';
import { useReminderItem } from '../../hooks/useReminderItem';
import { setReminderMutationFn, unsetReminderMutationFn, useUserReminders } from '../../store/reducers/reminderSlice';
import { getUserInfo } from '../../utils/user';
import { isGuestUser } from '../../config/guestMode';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as braze from '@braze/web-sdk';
import h from '../../helpers/common';
import { reminderCallback } from '../../hooks/useReminderState';
import { useContext } from 'react';
import { MixpanelContext } from '../../contexts/MixpanelContext';

const ReminderButton = ({
    children = null,
    className = '',
    buttonType,
    item,
    onClick = () => {
        return;
    },
}) => {
    const newItem = {
        ...item?.ppvEvent?.program,
        paId: item?.ppvEvent?.program?.id,
        isPpv: true,
        name: item?.ppvEvent?.name,
        eventId: item?.ppvEvent?.eventId,
    };
    const programTitle: string = newItem?.name;
    const itemCategory = buttonType;

    const userInfo = getUserInfo();
    const queryClient = useQueryClient();
    const { track }: any = useContext(MixpanelContext);

    const { data: reminderData } = useUserReminders({ detailed: false }, userInfo);
    const { remindersList, paId, listingId, isReminded, reminderId } = useReminderItem(newItem, reminderData ?? []);

    const setReminderMutation = useMutation({
        mutationFn: setReminderMutationFn,
        onSuccess: ({ data }) => {
            queryClient.invalidateQueries({ queryKey: ['profiles', 'user-reminders'] });
            queryClient.invalidateQueries({ queryKey: ['remindersList', itemCategory] });
            braze?.getUser()?.addToCustomAttributeArray('reminder_programs_listing_ids', listingId);
            braze?.getUser()?.setLanguage(h.getLang());
            const mixpanelProperties = reminderItemMixpanelProperties(itemCategory, newItem, null);
            reminderCallback(mixpanelProperties, 'set', null, newItem, track, true, data.notifChannels);
        },
        onError: (error) => {
            const mixpanelProperties = reminderItemMixpanelProperties(itemCategory, newItem, null);
            reminderCallback(mixpanelProperties, 'errorSet', error, newItem, track, true, []);
        },
    });

    const unsetReminderMutation = useMutation({
        mutationFn: unsetReminderMutationFn,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['profiles', 'user-reminders'] });
            queryClient.invalidateQueries({ queryKey: ['remindersList', itemCategory] });
            braze?.getUser()?.removeFromCustomAttributeArray('reminder_programs_listing_ids', listingId);
            const mixpanelProperties = reminderItemMixpanelProperties(itemCategory, newItem, null);
            reminderCallback(mixpanelProperties, 'unset', null, newItem, track, true, []);
        },
        onError: (error) => {
            const mixpanelProperties = reminderItemMixpanelProperties(itemCategory, newItem, null);
            reminderCallback(mixpanelProperties, 'errorUnset', error, newItem, track, true, []);
        },
    });

    if (isGuestUser(userInfo)) {
        return null;
    }

    switch (buttonType) {
        case ButtonTypes.PPV:
            return (
                <>
                    {checkIfReminderExists(remindersList, paId, listingId) ? (
                        <Button
                            loading={unsetReminderMutation.isPending}
                            disabled={true}
                            social
                            secondary
                            onClick={() => {
                                unsetReminderMutation.mutate({
                                    reminderId,
                                    listingId,
                                    programTitle,
                                    index: 0,
                                    user: userInfo,
                                });
                            }}
                        >
                            {children}
                            <FontIcon name="check-green" />
                        </Button>
                    ) : (
                        <Button
                            loading={setReminderMutation.isPending}
                            social
                            secondary
                            onClick={() => {
                                setReminderMutation.mutate({
                                    paId,
                                    listingId,
                                    programTitle,
                                    index: 0,
                                    type: itemCategory,
                                    user: userInfo,
                                    language: h.getLang(),
                                });
                            }}
                        >
                            {children}
                            <FontIcon name="bell" size="smaller" md="small"></FontIcon>
                        </Button>
                    )}
                </>
            );

        default:
            return (
                <a onClick={onClick} className={className}>
                    {children}
                </a>
            );
    }
};

export default ReminderButton;
